import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { differenceInMinutes } from 'date-fns';
import { apiClient } from '../api/client';
import { useSessionStore, type ActiveSession } from '../store/sessionStore';
import { useAuthStore } from '../store/authStore';
import { analytics } from '../hooks/useAnalytics';
import { AvailabilityPill } from '../components/ui/AvailabilityPill';
import { CallButton } from '../components/ui/CallButton';
import { formatLocalTime, isTodayInTz } from '../utils/time';
import { useTranslation } from '../i18n';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';
import { getWsBaseUrl } from '../lib/wsUrl';

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtElapsed(s: number) {
  const h   = Math.floor(s / 3600);
  const m   = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((n) => String(n).padStart(2, '0')).join(':');
}

function elapsedFrom(startedAt: string) {
  return Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
}

function canRequestExtension(startedAt: string, availableUntil: string): boolean {
  const now = Date.now();
  const sessionStart = new Date(startedAt).getTime();
  const sessionEnd = new Date(availableUntil).getTime();

  const totalDurationMs = sessionEnd - sessionStart;
  const timeRemainingMs = sessionEnd - now;
  const twentyPercentMs = totalDurationMs * 0.2;

  return timeRemainingMs > twentyPercentMs;
}

// ─── Session ended view ───────────────────────────────────────────────────────

interface EndedData {
  id: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  status: string;
  spotLabel: string;
}

interface ExtensionTimeoutModal {
  visible: boolean;
  message?: string;
}

interface ExtensionDurationModal {
  visible: boolean;
  selectedDuration: number; // in minutes
}

interface ScheduledSession {
  id: string;
  status: string;
  spot: {
    id: string;
    spot_number: string;
    level_label: string;
    level_code: string;
    description: string | null;
    owner: { apartment: string };
  };
  scheduled_start_at: string;
  claimed_at: string;
  reason?: string;
}

function SessionEndedView({ data, t }: { data: EndedData; t: (key: string, params?: Record<string, string | number>) => string }) {
  const navigate = useNavigate();
  const timezone = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');
  const { language } = useTranslation();
  const durMin   = Math.floor(data.duration_seconds / 60);
  // Show day + month if the session ended on a different calendar date than
  // today (e.g. an overnight or multi-day claim that just ended).
  const endedDisplay = isTodayInTz(data.ended_at, timezone)
    ? formatLocalTime(data.ended_at, timezone, 'time', language)
    : formatLocalTime(data.ended_at, timezone, 'datetime', language);

  return (
    <div style={{ ...page, alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      {/* Green checkmark */}
      <div style={{
        width:           '64px',
        height:          '64px',
        borderRadius:    '50%',
        backgroundColor: '#dcfce7',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        fontSize:        '32px',
        marginBottom:    '16px',
      }}>
        ✓
      </div>
      <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>{t('session_ended')}</div>
      <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', marginBottom: '24px' }}>
        {t('thanks_freeing_spot')} {data.spotLabel}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', width: '100%', maxWidth: '320px' }}>
        <div style={{ ...statCard, flex: 1 }}>
          <div style={{ fontSize: '20px', fontWeight: 700 }}>{durMin} min</div>
          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{t('duration')}</div>
        </div>
        <div style={{ ...statCard, flex: 1 }}>
          <div style={{ fontSize: '20px', fontWeight: 700 }}>{endedDisplay}</div>
          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{t('ended_at')}</div>
        </div>
      </div>

      <button onClick={() => navigate('/')} style={{ ...btn, maxWidth: '320px', marginBottom: '8px' }}>
        {t('back_to_home')}
      </button>
      <button
        onClick={() => navigate('/find')}
        style={{ ...btn, maxWidth: '320px', background: '#f1f4f6', color: '#374151' }}
      >
        {t('find_another')}
      </button>
    </div>
  );
}

// ─── SessionPage ──────────────────────────────────────────────────────────────

export default function SessionPage() {
  const navigate = useNavigate();
  const qc       = useQueryClient();
  const { t, language } = useTranslation();
  const { reclaimAlert, window15MinWarning, setReclaimAlert, clearReclaimAlert } =
    useSessionStore();
  const timezone = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');

  /**
   * Show only the time when the moment falls on today's calendar date in the
   * building TZ; otherwise show day + month + time so a multi-day claim or
   * a midnight-crossing session reads correctly.
   */
  const fmtAware = (iso: string) =>
    isTodayInTz(iso, timezone)
      ? formatLocalTime(iso, timezone, 'time', language)
      : formatLocalTime(iso, timezone, 'datetime', language);

  const [elapsed,   setElapsed]   = useState(0);
  const [endedData, setEndedData] = useState<EndedData | null>(null);
  const [ackDone,   setAckDone]   = useState(false);
  const [sessionIndex, setSessionIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'active' | 'scheduled'>('active');
  const [scheduledSessions, setScheduledSessions] = useState<ScheduledSession[]>([]);
  const [extensionTimeout, setExtensionTimeout] = useState<ExtensionTimeoutModal>({ visible: false });
  const [extensionDuration, setExtensionDuration] = useState<ExtensionDurationModal>({ visible: false, selectedDuration: 30 });

  // Fetch on mount and poll every 15 s as WS fallback
  const { isLoading, data: fetchedSessions } = useQuery({
    queryKey:        ['session', 'active'],
    queryFn:         () => apiClient.get<{ data: ActiveSession[] }>('/sessions/active').then((r) => r.data.data),
    refetchInterval: 15_000,
  });

  // Fetch scheduled sessions
  const { data: fetchedScheduled } = useQuery({
    queryKey:        ['session', 'scheduled'],
    queryFn:         () => apiClient.get<{ data: ScheduledSession[] }>('/sessions/scheduled').then((r) => r.data.data),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (fetchedSessions !== undefined) {
      const { setActiveSessions } = useSessionStore.getState();
      setActiveSessions(fetchedSessions);
      // Always sync reclaim alert from server — removes the race where WS fires before fetch
      if (fetchedSessions.length > 0 && fetchedSessions[0]?.reclaim_alert) {
        setReclaimAlert(fetchedSessions[0].reclaim_alert);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedSessions]);

  useEffect(() => {
    if (fetchedScheduled !== undefined) {
      setScheduledSessions(fetchedScheduled);
    }
  }, [fetchedScheduled]);

  // Redirect if no sessions (after loading)
  const activeSessions = useSessionStore((s) => s.activeSessions);
  useEffect(() => {
    if (!isLoading && activeSessions.length === 0 && scheduledSessions.length === 0 && !endedData) {
      navigate('/', { replace: true });
    }
  }, [isLoading, activeSessions.length, scheduledSessions.length, endedData, navigate]);

  // Track reclaim alert view
  useEffect(() => {
    if (reclaimAlert && viewMode === 'active') {
      const currentSession = activeSessions[sessionIndex % activeSessions.length];
      analytics.trackReclaimAlertViewed(currentSession?.spot.id || '');
    }
  }, [reclaimAlert?.id, viewMode, activeSessions, sessionIndex]);

  // Listen for extension timeout via WebSocket
  useEffect(() => {
    const token = useAuthStore.getState().accessToken;
    if (!token) return;

    const url = `${getWsBaseUrl()}/ws?token=${token}`;
    const ws = new WebSocket(url);

    ws.onmessage = (e: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(e.data) as { event: string; data: { message?: string } };
        if (msg.event === 'extension.rejected_by_timeout') {
          analytics.trackSessionExtensionTimeout();
          setExtensionTimeout({
            visible: true,
            message: msg.data.message,
          });
        } else if (msg.event === 'extension.rejected') {
          analytics.trackSessionExtensionRejected(msg.data.message);
        } else if (msg.event === 'extension.approved') {
          // Owner approved the extension request
          const minutes = (msg.data as { granted_minutes?: number }).granted_minutes;
          analytics.trackSessionExtensionApproved(minutes ?? 0);
        }
      } catch {
        // ignore malformed messages
      }
    };

    return () => ws.close();
  }, []);

  // Elapsed timer
  useEffect(() => {
    if (!activeSessions.length) return;
    const current = activeSessions[sessionIndex % activeSessions.length];
    setElapsed(elapsedFrom(current.started_at));
    const t = setInterval(() => setElapsed(elapsedFrom(current.started_at)), 1000);
    return () => clearInterval(t);
  }, [activeSessions, sessionIndex]);

  // End session mutation (for active sessions)
  const endMutation = useMutation({
    mutationFn: () => {
      const { activeSessions: sessions } = useSessionStore.getState();
      const current = sessions[sessionIndex % sessions.length];
      return apiClient
        .patch<{ data: { id: string; started_at: string; ended_at: string; duration_seconds: number; status: string } }>(
          `/sessions/${current.id}/end`,
        )
        .then((r) => r.data.data);
    },
    onSuccess: (ended) => {
      const durationMinutes = Math.floor(ended.duration_seconds / 60);
      analytics.trackSessionEnded(durationMinutes);
      const { activeSessions: sessions } = useSessionStore.getState();
      const current = sessions[sessionIndex % sessions.length];
      // If a reclaim alert was active and never acknowledged, mark it as ignored
      if (reclaimAlert && !ackDone) {
        analytics.trackReclaimAlertIgnored(current?.spot.id || '');
      }
      const { clearSession: clearSingleSession } = useSessionStore.getState();
      clearSingleSession(current.id);

      // Only show ended view if no other sessions
      const { activeSessions: remaining } = useSessionStore.getState();
      if (remaining.length === 0) {
        setEndedData({
          ...ended,
          spotLabel: `${t(current.spot.level_code) || current.spot.level_label} · ${current.spot.spot_number}`,
        });
      }
      void qc.invalidateQueries({ queryKey: ['spots', 'free'] });
    },
  });

  // Request extension mutation
  const extensionMutation = useMutation({
    mutationFn: () => {
      const { activeSessions: sessions } = useSessionStore.getState();
      const current = sessions[sessionIndex % sessions.length];
      if (!('available_until' in current) || !current.available_until) {
        return Promise.reject(new Error('No availability end time'));
      }
      // Request extension with selected duration in minutes
      const extensionMs = extensionDuration.selectedDuration * 60 * 1000;
      const newEndTime = new Date(new Date().getTime() + extensionMs).toISOString();
      analytics.trackSessionExtensionRequested(extensionDuration.selectedDuration);
      setExtensionDuration({ visible: false, selectedDuration: 30 });
      return apiClient
        .post(`/sessions/${current.id}/request-extension`, {
          requested_end_time: newEndTime,
        })
        .then((r) => r.data.data);
    },
    onSuccess: () => {
      // Note: approval is tracked via WS `extension.approved` event, not here.
      // POST success only means the request was created.
      void qc.invalidateQueries({ queryKey: ['session', 'active'] });
    },
    onError: (error: any) => {
      const code = error.response?.data?.error?.code;
      if (code === 'extension_window_closed') {
        analytics.trackSessionExtensionRejected('extension_window_closed');
        setExtensionTimeout({
          visible: true,
          message: t('extension_window_closed'),
        });
      } else if (code) {
        analytics.trackSessionExtensionRejected(code);
      }
    },
  });

  // Cancel scheduled session mutation
  const cancelMutation = useMutation({
    mutationFn: () => {
      const current = scheduledSessions[sessionIndex % scheduledSessions.length];
      return apiClient.patch(`/sessions/${current.id}/end`);
    },
    onSuccess: () => {
      const current = scheduledSessions[sessionIndex % scheduledSessions.length];
      setScheduledSessions(prev => prev.filter(s => s.id !== current.id));
      void qc.invalidateQueries({ queryKey: ['spots', 'free'] });
    },
  });

  // Acknowledge reclaim
  // const ackMutation = useMutation({
  //   mutationFn: () => {
  //     if (!reclaimAlert?.id) return Promise.resolve({ data: null });
  //     return apiClient.patch(`/reclaim-alerts/${reclaimAlert.id}/acknowledge`);
  //   },
  //   onSuccess: () => {
  //     setAckDone(true);
  //     setTimeout(() => {
  //       clearReclaimAlert();
  //       setAckDone(false);
  //     }, 1500);
  //   },
  // });
  const ackMutation = useMutation({
  mutationFn: async () => {
    if (!reclaimAlert?.id) return null;
    const currentSession = activeSessions[sessionIndex % activeSessions.length];
    analytics.trackReclaimAlertAcknowledged(currentSession?.spot.id || '');
    return apiClient.patch(`/reclaim-alerts/${reclaimAlert.id}/acknowledge`).then((r) => r.data);
  },
  onSuccess: () => {
    setAckDone(true);
    setTimeout(() => {
      clearReclaimAlert();
      setAckDone(false);
    }, 1500);
  },
});

  // Show ended state
  if (endedData) return <SessionEndedView data={endedData} t={t} />;

  // Determine which sessions to display based on view mode
  const displaySessions = viewMode === 'active' ? activeSessions : scheduledSessions;
  const currentSession = displaySessions.length > 0 ? displaySessions[sessionIndex % displaySessions.length] : null;

  if (isLoading || !currentSession) {
    return (
      <div style={{ ...page, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#9ca3af' }}>{t('loading')}</div>
      </div>
    );
  }

  const session      = currentSession;
  const spotLabel    = `${t(session.spot.level_code) || session.spot.level_label} · ${session.spot.spot_number}`;
  const minsRemaining = viewMode === 'active' && 'available_until' in session && session.available_until
    ? differenceInMinutes(new Date(session.available_until), new Date())
    : null;
  const showWarning  = minsRemaining !== null && minsRemaining > 0 && minsRemaining < 30;

  return (
    <div style={page}>
      {/* Top bar */}
      <div style={topBar}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '0', color: '#005bc0' }}
          aria-label="Back"
        >
          <DirectionalIcon icon="arrow_back" style={{ fontSize: '24px' }} />
        </button>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '22px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="18" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="18" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        {activeSessions.length > 1 ? (
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {sessionIndex + 1}/{activeSessions.length}
          </div>
        ) : (
          <div style={{ width: '32px' }} />
        )}
      </div>

      {/* Tabs for switching between active and scheduled */}
      {(activeSessions.length > 0 || scheduledSessions.length > 0) && (
        <div style={{
          display: 'flex',
          gap: '0',
          backgroundColor: '#f3f4f6',
          borderBottom: '1px solid #e5e7eb',
        }}>
          {activeSessions.length > 0 && (
            <button
              onClick={() => { setViewMode('active'); setSessionIndex(0); }}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                background: viewMode === 'active' ? '#fff' : 'transparent',
                borderBottom: viewMode === 'active' ? '2px solid #005bc0' : 'none',
                color: viewMode === 'active' ? '#005bc0' : '#9ca3af',
                cursor: 'pointer',
                fontWeight: viewMode === 'active' ? 600 : 500,
                fontSize: '13px',
                transition: 'all 0.15s',
              }}
            >
              {t('active')} ({activeSessions.length})
            </button>
          )}
          {scheduledSessions.length > 0 && (
            <button
              onClick={() => { setViewMode('scheduled'); setSessionIndex(0); }}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                background: viewMode === 'scheduled' ? '#fff' : 'transparent',
                borderBottom: viewMode === 'scheduled' ? '2px solid #005bc0' : 'none',
                color: viewMode === 'scheduled' ? '#005bc0' : '#9ca3af',
                cursor: 'pointer',
                fontWeight: viewMode === 'scheduled' ? 600 : 500,
                fontSize: '13px',
                transition: 'all 0.15s',
              }}
            >
              {t('scheduled')} ({scheduledSessions.length})
            </button>
          )}
        </div>
      )}

      {displaySessions.length > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: '#f3f4f6',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <button
            onClick={() => setSessionIndex((i) => (i - 1 + displaySessions.length) % displaySessions.length)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '18px' }}
            aria-label="Previous session"
          >
            ‹
          </button>
          <div style={{ fontSize: '12px', color: '#9ca3af', flex: 1, textAlign: 'center' }}>
            {`${session.spot.spot_number} (${t(session.spot.level_code) || session.spot.level_label})`}
          </div>
          <button
            onClick={() => setSessionIndex((i) => (i + 1) % displaySessions.length)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '18px' }}
            aria-label="Next session"
          >
            ›
          </button>
        </div>
      )}

      <div style={body}>
        {/* Spot label */}
        <div style={{ textAlign: 'center', marginBottom: '24px', paddingTop: '8px' }}>
          <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{spotLabel}</div>
          {session.spot.description && (
            <div style={{ fontSize: '14px', color: '#6b7280' }}>{session.spot.description}</div>
          )}
        </div>

        {/* Availability pill (active sessions only) */}
        {viewMode === 'active' && 'available_until' in session && session.available_until && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <AvailabilityPill
              source={('reclaim_alert' in session && session.reclaim_alert ? 'window' : 'schedule') as 'schedule' | 'window'}
              until={session.available_until}
              timezone={timezone}
            />
          </div>
        )}

        {/* Timer or scheduled time */}
        {viewMode === 'active' ? (
          <div style={{
            textAlign:  'center',
            marginBottom: '8px',
          }}>
            <div style={{
              fontFamily:    "'Manrope', system-ui, sans-serif",
              fontSize:      '52px',
              fontWeight:    800,
              color:         '#111',
              letterSpacing: '-1px',
            }}>
              {fmtElapsed(elapsed)}
            </div>
            <div style={{ fontSize: '13px', color: '#9ca3af' }}>
              {t('started_at')} {'started_at' in session ? fmtAware(session.started_at) : ''}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            background: '#dbeafe',
            borderRadius: '12px',
            marginBottom: '20px',
          }}>
            <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: 500, marginBottom: '6px' }}>
              {t('scheduled_to_start')}
            </div>
            <div style={{
              fontFamily: "'Manrope', system-ui, sans-serif",
              fontSize: '28px',
              fontWeight: 700,
              color: '#1e40af',
            }}>
              {formatLocalTime((session as ScheduledSession).scheduled_start_at, timezone, 'datetime', language)}
            </div>
          </div>
        )}

        {/* 15 min urgent warning (active sessions only) */}
        {viewMode === 'active' && window15MinWarning && (
          <div style={{
            ...alertBox,
            backgroundColor: '#fee2e2',
            marginBottom:    '12px',
            border:          '1px solid #fecaca',
          }}>
            <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '4px' }}>
              {t('urgent_reclaim_warning')}
            </div>
            <div style={{ fontSize: '13px', color: '#991b1b' }}>
              {t('session_ending_soon')}
            </div>
          </div>
        )}

        {/* < 30 min warning (only show if not already showing 15 min warning, active sessions only) */}
        {viewMode === 'active' && showWarning && !window15MinWarning && (
          <div style={{
            ...alertBox,
            backgroundColor: '#fef3c7',
            marginBottom:    '12px',
          }}>
            <div style={{ fontWeight: 600, color: '#92400e' }}>
              {t('available_until')} {'available_until' in session && session.available_until ? fmtAware(session.available_until) : ''}
            </div>
            <div style={{ fontSize: '13px', color: '#b45309' }}>
              {t('minutes_remaining', { minsRemaining })}
            </div>
          </div>
        )}

        {/* Reclaim alert banner (active sessions only) */}
        {viewMode === 'active' && reclaimAlert && (
          <div style={{
            ...alertBox,
            backgroundColor: ackDone ? '#dcfce7' : '#fff7ed',
            marginBottom:    '12px',
          }}>
            {ackDone ? (
              <div style={{ fontWeight: 600, color: '#16a34a', fontSize: '14px' }}>
                {t('acknowledged_reclaim')}
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 600, color: '#ea580c', marginBottom: '6px' }}>
                  {t('owner_needs_spot')}
                </div>
                <div style={{ fontSize: '13px', color: '#374151', marginBottom: '10px' }}>
                  {t('vacate_when_you_can')}
                </div>
                <button
                  onClick={() => ackMutation.mutate()}
                  disabled={ackMutation.isPending || !reclaimAlert?.id}
                  style={{ ...btn, height: '38px', background: 'linear-gradient(135deg, #ea580c, #c2410c)' }}
                >
                  {ackMutation.isPending ? t('sending') : t('ok_ill_move_soon')}
                </button>
              </>
            )}
          </div>
        )}

        {/* Extension request button (active sessions only) */}
        {viewMode === 'active' && 'available_until' in session && session.available_until && (
          <div style={{ marginTop: '16px', marginBottom: '8px' }}>
            <button
              onClick={() => setExtensionDuration({ visible: true, selectedDuration: 30 })}
              disabled={extensionMutation.isPending || !canRequestExtension(session.started_at, session.available_until)}
              style={{
                ...btn,
                background: canRequestExtension(session.started_at, session.available_until)
                  ? 'linear-gradient(135deg, #16a34a, #15803d)'
                  : '#d1d5db',
                color: canRequestExtension(session.started_at, session.available_until) ? '#f7f7ff' : '#6b7280',
              }}
            >
              {extensionMutation.isPending ? t('requesting') : t('can_i_stay_longer')}
            </button>
            {!canRequestExtension(session.started_at, session.available_until) && (
              <p style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                {t('extension_window_closed')}
              </p>
            )}
          </div>
        )}

        {/* End session or Cancel scheduled claim */}
        <div style={{ marginTop: '12px' }}>
          {viewMode === 'active' ? (
            <>
              <button
                onClick={() => endMutation.mutate()}
                disabled={endMutation.isPending}
                style={{ ...btn, background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
              >
                {endMutation.isPending ? t('ending') : t('end_my_session')}
              </button>
              {endMutation.isError && (
                <p style={{ textAlign: 'center', color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>
                  {t('error_try_again')}
                </p>
              )}
              <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginTop: '12px' }}>
                {t('contact_owner_via_chat')}
              </p>
            </>
          ) : (
            <>
              <button
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
                style={{ ...btn, background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
              >
                {cancelMutation.isPending ? t('cancelling') : t('cancel_claim')}
              </button>
              {cancelMutation.isError && (
                <p style={{ textAlign: 'center', color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>
                  {t('error_try_again')}
                </p>
              )}
              <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginTop: '12px' }}>
                {t('cancel_future_claim_desc')}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Extension duration picker modal */}
      {extensionDuration.visible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '320px',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '24px', color: '#111' }}>
              {t('how_long_extend')}
            </div>

            {/* Duration picker */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              marginBottom: '32px',
            }}>
              {/* Minus button */}
              <button
                onClick={() => setExtensionDuration((prev) => ({
                  ...prev,
                  selectedDuration: Math.max(15, prev.selectedDuration - 15),
                }))}
                disabled={extensionDuration.selectedDuration <= 15}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '8px',
                  border: 'none',
                  background: extensionDuration.selectedDuration <= 15 ? '#e5e7eb' : '#f3f4f6',
                  color: extensionDuration.selectedDuration <= 15 ? '#d1d5db' : '#374151',
                  fontSize: '24px',
                  fontWeight: 700,
                  cursor: extensionDuration.selectedDuration <= 15 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                −
              </button>

              {/* Duration display */}
              <div style={{
                fontSize: '36px',
                fontWeight: 800,
                color: '#005bc0',
                minWidth: '100px',
                fontFamily: "'Manrope', system-ui, sans-serif",
              }}>
                {extensionDuration.selectedDuration}m
              </div>

              {/* Plus button */}
              <button
                onClick={() => setExtensionDuration((prev) => ({
                  ...prev,
                  selectedDuration: Math.min(60, prev.selectedDuration + 15),
                }))}
                disabled={extensionDuration.selectedDuration >= 60}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '8px',
                  border: 'none',
                  background: extensionDuration.selectedDuration >= 60 ? '#e5e7eb' : '#f3f4f6',
                  color: extensionDuration.selectedDuration >= 60 ? '#d1d5db' : '#374151',
                  fontSize: '24px',
                  fontWeight: 700,
                  cursor: extensionDuration.selectedDuration >= 60 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                +
              </button>
            </div>

            {/* Duration presets as info */}
            <div style={{
              fontSize: '12px',
              color: '#9ca3af',
              marginBottom: '24px',
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}>
              <span style={{ padding: '4px 8px', background: '#f3f4f6', borderRadius: '4px' }}>15m</span>
              <span style={{ padding: '4px 8px', background: '#f3f4f6', borderRadius: '4px' }}>30m</span>
              <span style={{ padding: '4px 8px', background: '#f3f4f6', borderRadius: '4px' }}>45m</span>
              <span style={{ padding: '4px 8px', background: '#f3f4f6', borderRadius: '4px' }}>60m</span>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setExtensionDuration({ visible: false, selectedDuration: 30 })}
                style={{
                  flex: 1,
                  height: '44px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#f3f4f6',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => extensionMutation.mutate()}
                disabled={extensionMutation.isPending}
                style={{
                  flex: 1,
                  height: '44px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  color: '#f7f7ff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: extensionMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: extensionMutation.isPending ? 0.7 : 1,
                }}
              >
                {extensionMutation.isPending ? t('requesting') : t('ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extension timeout modal */}
      {extensionTimeout.visible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '320px',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏱️</div>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px', color: '#111' }}>
              {t('extension_timed_out')}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.5' }}>
              {extensionTimeout.message || t('extension_request_timed_out_message')}
            </div>
            <button
              onClick={() => setExtensionTimeout({ visible: false })}
              style={{
                ...btn,
                maxWidth: '100%',
                background: 'linear-gradient(135deg, #005bc0, #004fa9)',
              }}
            >
              {t('ok_got_it')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const page: React.CSSProperties = {
  display:         'flex',
  flexDirection:   'column',
  minHeight:       '100dvh',
  maxWidth:        '430px',
  margin:          '0 auto',
  backgroundColor: '#f8f9fa',
  fontFamily:      "'Inter', system-ui, -apple-system, sans-serif",
};

const topBar: React.CSSProperties = {
  display:              'flex',
  alignItems:           'center',
  justifyContent:       'space-between',
  padding:              '0 16px',
  height:               '44px',
  backgroundColor:      'rgba(248,249,250,0.85)',
  backdropFilter:       'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  position:             'sticky',
  top:                  0,
  zIndex:               10,
};

const body: React.CSSProperties = {
  flex:      1,
  padding:   '16px',
  overflowY: 'auto',
};

const alertBox: React.CSSProperties = {
  borderRadius: '14px',
  padding:      '14px 16px',
};

const btn: React.CSSProperties = {
  display:      'block',
  width:        '100%',
  height:       '44px',
  borderRadius: '8px',
  border:       'none',
  background:   'linear-gradient(135deg, #005bc0, #004fa9)',
  color:        '#f7f7ff',
  fontSize:     '14px',
  fontWeight:   600,
  cursor:       'pointer',
};

const statCard: React.CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius:    '14px',
  boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.06)',
  padding:         '12px',
  textAlign:       'center',
};
